/**
 * Pop-out entry. `/launch#lt=` exchanges the token once; any other path restores the stored
 * session from IndexedDB (push wake, reload). The token leaves the address bar before the
 * exchange starts.
 */
import { render } from 'preact';
import './styles.css';
import { ApiClient } from './app/api.js';
import { browserId, exchangeLaunchToken, LaunchError, parseLaunchFragment, storedSessionUsable, toStoredSession } from './app/launch.js';
import { PhoneApp } from './app/phone.js';
import { openSessionStore, type StoredSession } from './app/session-store.js';
import { APP_VERSION, readEnv } from './env.js';
import { createLogger } from './log.js';
import { App, BootView, RelaunchView } from './ui/App.jsx';

const env = readEnv();
const log = createLogger({ level: env.logLevel, service: 'phone', fields: { version: APP_VERSION } });
const root = document.getElementById('app')!;
const store = openSessionStore();

function status(text: string): void {
  render(<BootView status={text} />, root);
}

async function boot(): Promise<void> {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  let session: StoredSession | null = null;
  if (path === '/launch') {
    const lt = parseLaunchFragment(window.location.hash);
    window.history.replaceState(null, '', '/');
    if (!lt) {
      render(<RelaunchView reason="not_found" />, root);
      return;
    }
    status('Signing in…');
    const api = new ApiClient({ baseUrl: env.apiBase });
    const bid = browserId(safeStorage());
    try {
      const t0 = Date.now();
      const launch = await exchangeLaunchToken({ api, lt, browserId: bid, onRetry: () => status('Could not sign the phone in — retrying') });
      const offset = Date.parse(launch.server_time) - (t0 + Date.now()) / 2;
      session = toStoredSession(launch, bid, Number.isFinite(offset) ? offset : 0);
      await store.put(session).catch((e: Error) => log.warn({ err: e.message }, 'session store write failed'));
    } catch (e) {
      const reason = e instanceof LaunchError ? e.kind : 'network';
      log.warn({ err: (e as Error).message, reason }, 'launch failed');
      render(<RelaunchView reason={reason} />, root);
      return;
    }
  } else {
    status('Restoring your session…');
    session = await store.get().catch(() => null);
    if (!session || !storedSessionUsable(session, Date.now() + session.clock_offset_ms)) {
      render(<RelaunchView reason="no_session" />, root);
      return;
    }
  }
  const app = new PhoneApp({
    env,
    log: log.child({ tenant_id: session.launch.agent.tenant_id, agent_id: session.launch.agent.id }),
    store,
    session,
    vapidPublicKey: typeof import.meta.env['CC_VAPID_PUBLIC_KEY'] === 'string' ? (import.meta.env['CC_VAPID_PUBLIC_KEY'] as string) : null,
  });
  render(<App app={app} />, root);
  await app.start();
  // the login click normally happened in the dock; try to unlock sound now, the strip covers a refusal
  void app.enableSound();
}

function safeStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

void boot();
